/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PbxCliPage.tsx (PBX CLI Terminal Page)
 🗓️ Created: 2025-01-28
 🧩 Description:
    React component for PBX CLI terminal interface using xterm.js.
    Provides real-time terminal access to FreeSWITCH CLI and safe system commands.

 🔧 Notes:
    - Uses xterm.js for terminal emulation
    - Connects via WebSocket to backend
    - Implements command history and autocomplete

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { Terminal as TerminalIcon, AlertCircle } from 'lucide-react';
import { fetchAllPbxCliButtonConfigs } from './api';
import ButtonConfigModal, { ButtonConfig } from './ButtonConfigModal';
import { useSidebarState } from '../../hooks/useSidebarState';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import CommandSlotButton from './CommandSlotButton';
import { buildFallbackCommandGroups } from './pbxCliCommands';
import { PbxCommandSlotGrid } from './PbxCommandSlotGrid';

type FitAddonInstance = InstanceType<typeof FitAddon>;

interface CommandDefinition {
  command: string;
  description: string;
}

export default function PbxCliPage() {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const { width: sidebarWidth } = useSidebarState();
  const effectiveSidebarWidth = sidebarWidth === 0 ? 350 : sidebarWidth;

  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstanceRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddonInstance | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const commandHistoryRef = useRef<string[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const initAttemptsRef = useRef(0);
  const isInitializingRef = useRef(false);

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buttonConfigs, setButtonConfigs] = useState<ButtonConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [editingSlot, setEditingSlot] = useState<number | null>(null);

  const pageTitle = useMemo(() => t('pbxCli.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const fsCliCommands = useMemo<CommandDefinition[]>(() => {
    const raw = t('pbxCli.terminal.commands', { returnObjects: true }) as unknown;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return [];
    }
    const commands = raw as Record<string, { command?: string; description?: string }>;
    return Object.values(commands)
      .map(({ command, description }) => ({ command: command ?? '', description: description ?? '' }))
      .filter((entry) => entry.command.length > 0);
  }, [t]);

  const fallbackCommandGroups = useMemo(() => {
    const raw = t('pbxCli.terminal.fallbackGroups', { returnObjects: true }) as unknown;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
    return buildFallbackCommandGroups(raw as Parameters<typeof buildFallbackCommandGroups>[0]);
  }, [t]);

  const buttonSlots = useMemo(
    () =>
      Array.from({ length: 24 }, (_, index) => {
        const config = buttonConfigs.find((button) => button.orderIndex === index);
        return { slotIndex: index, config: config || null };
      }),
    [buttonConfigs],
  );

  const showErrorRef = useRef(showError);
  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadErrorMessage = useMemo(() => t('pbxCli.buttonConfigs.messages.loadError'), [t]);

  const loadButtonConfigs = useCallback(async () => {
    setLoadingConfigs(true);
    try {
      const configs = await fetchAllPbxCliButtonConfigs();
      const normalized = configs.map((config) => ({
        id: config.id,
        uuid: config.uuid,
        label: config.label,
        tooltip: config.tooltip,
        command: config.command,
        orderIndex: config.orderIndex,
        enabled: config.enabled,
      }));
      setButtonConfigs(normalized);
    } catch (err) {
      showErrorRef.current?.(err instanceof Error ? err.message : loadErrorMessage);
      setButtonConfigs([]);
    } finally {
      setLoadingConfigs(false);
    }
  }, [loadErrorMessage]);

  useEffect(() => {
    loadButtonConfigs();
  }, [loadButtonConfigs]);

  useLayoutEffect(() => {
    if (terminalInstanceRef.current || !terminalRef.current) {
      return;
    }

    let isActive = true;
    let initTimeout: number | null = null;
    let retryTimeout: number | null = null;
    let fitTimeout: number | null = null;
    let resizeTimeout: number | null = null;

    const disposeTerminal = () => {
      if (terminalInstanceRef.current) {
        try {
          terminalInstanceRef.current.dispose();
        } catch {
          /* ignore dispose errors */
        }
        terminalInstanceRef.current = null;
      }
      fitAddonRef.current = null;
    };

    const closeWebSocket = () => {
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          /* ignore ws errors */
        }
        wsRef.current = null;
      }
    };

    const writeError = (message: string) => {
      terminalInstanceRef.current?.writeln(`\r\n\x1b[31m✗ ${message}\x1b[0m\r\n`);
    };

    const connectWebSocket = (term: Terminal, fitAddon: FitAddonInstance) => {
      // Authentication is handled via HTTP-only cookies
      // The WebSocket will automatically include cookies with the request
      const rawApiUrl = import.meta.env.VITE_API_URL;
      const apiUrl = (rawApiUrl && rawApiUrl.startsWith('http')) ? rawApiUrl : window.location.origin;
      const wsProtocol = apiUrl.startsWith('https') ? 'wss:' : 'ws:';
      const wsHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      const wsUrl = `${wsProtocol}//${wsHost}/ws/pbx-cli`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!isActive) return;
        setIsConnected(true);
        setError(null);
        term.writeln(`\x1b[32m✓ ${t('pbxCli.connected')}\x1b[0m\r\n`);
      };

      ws.onmessage = (event) => {
        const writeData = (text: string) => term.write(text);
        if (event.data instanceof Blob) {
          event.data.text().then(writeData);
        } else {
          writeData(event.data);
        }
      };

      ws.onerror = () => {
        if (!isActive) return;
        const message = t('pbxCli.terminal.messages.connectionError');
        setError(message);
        setIsConnected(false);
        writeError(message);
      };

      ws.onclose = (event) => {
        if (!isActive) return;
        setIsConnected(false);
        if (event.code !== 1000) {
          const reason = event.reason || t('pbxCli.disconnected');
          const message = t('pbxCli.terminal.messages.closed', { code: event.code, reason });
          setError(message);
          writeError(message);
        }
      };

      wsRef.current = ws;

      const sendResize = () => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        wsRef.current.send(
          JSON.stringify({
            type: 'resize',
            cols: term.cols,
            rows: term.rows,
          }),
        );
      };

      const fitTerminal = () => {
        if (!isActive || !fitAddonRef.current || !terminalInstanceRef.current) return;
        // Ensure terminal is fully opened before fitting
        const termElement = terminalRef.current?.querySelector('.xterm-screen');
        if (!termElement) {
          if (!isActive) return;
          fitTimeout = window.setTimeout(fitTerminal, 200);
          return;
        }
        try {
          fitAddonRef.current.fit();
          sendResize();
        } catch {
          if (!isActive) return;
          fitTimeout = window.setTimeout(fitTerminal, 200);
        }
      };

      fitTimeout = window.setTimeout(fitTerminal, 50);

      const handleResize = () => {
        if (resizeTimeout) window.clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(() => {
          if (!isActive || !fitAddonRef.current || !terminalInstanceRef.current) return;
          // Ensure terminal is fully rendered before fitting
          const termElement = terminalRef.current?.querySelector('.xterm-screen');
          if (!termElement) return;
          try {
            fitAddonRef.current.fit();
            sendResize();
          } catch {
            /* ignore */
          }
        }, 150);
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (resizeTimeout) {
          window.clearTimeout(resizeTimeout);
          resizeTimeout = null;
        }
      };
    };

    const registerTerminalListeners = (term: Terminal) => {
      let currentLine = '';

      term.onData((data) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          return;
        }

        const code = data.charCodeAt(0);

        if (code === 13 || data === '\r' || data === '\n') {
          term.write('\r\n');
          if (currentLine.trim()) {
            commandHistoryRef.current.push(currentLine);
            if (commandHistoryRef.current.length > 100) {
              commandHistoryRef.current.shift();
            }
            historyIndexRef.current = -1;
            wsRef.current.send(`${currentLine}\r`);
            currentLine = '';
          } else {
            wsRef.current.send('\r');
          }
          return;
        }

        if (code === 127 || code === 8) {
          if (currentLine.length > 0) {
            currentLine = currentLine.slice(0, -1);
            term.write('\b \b');
          }
          return;
        }

        if (data === '\x1b[A') {
          if (commandHistoryRef.current.length > 0) {
            if (historyIndexRef.current === -1) {
              historyIndexRef.current = commandHistoryRef.current.length - 1;
            } else if (historyIndexRef.current > 0) {
              historyIndexRef.current -= 1;
            }
            const historyCommand = commandHistoryRef.current[historyIndexRef.current];
            term.write('\r\x1b[K');
            currentLine = historyCommand;
            term.write(historyCommand);
          }
          return;
        }

        if (data === '\x1b[B') {
          if (historyIndexRef.current !== -1) {
            if (historyIndexRef.current < commandHistoryRef.current.length - 1) {
              historyIndexRef.current += 1;
              const historyCommand = commandHistoryRef.current[historyIndexRef.current];
              term.write('\r\x1b[K');
              currentLine = historyCommand;
              term.write(historyCommand);
            } else {
              historyIndexRef.current = -1;
              term.write('\r\x1b[K');
              currentLine = '';
            }
          }
          return;
        }

        if (code === 9) {
          const matches = fsCliCommands.filter((cmd) =>
            cmd.command.toLowerCase().startsWith(currentLine.toLowerCase()),
          );
          if (matches.length === 1) {
            const match = matches[0].command;
            const toAdd = match.slice(currentLine.length);
            currentLine = match;
            term.write(toAdd);
          } else if (matches.length > 1) {
            term.writeln('\r\n');
            matches.slice(0, 10).forEach((cmd) => {
              term.writeln(`  ${cmd.command} - ${cmd.description}`);
            });
            term.write(`\r\n${currentLine}`);
          }
          return;
        }

        if (code >= 32 && code <= 126) {
          currentLine += data;
          term.write(data);
        } else if (code !== 13) {
          wsRef.current?.send(data);
        }
      });
    };

    const attemptInitialization = () => {
      if (!isActive || !terminalRef.current || terminalInstanceRef.current || isInitializingRef.current) {
        return;
      }

      isInitializingRef.current = true;

      const host = terminalRef.current;
      const hostReady = () => {
        if (!host || !host.isConnected) return false;
        const rect = host.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return false;
        const style = window.getComputedStyle(host);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
          return false;
        }
        return true;
      };

      if (!hostReady()) {
        isInitializingRef.current = false;
        scheduleRetry();
        return;
      }

      let term: Terminal | null = null;
      let fitAddon: FitAddonInstance | null = null;

      try {
        term = new Terminal({
          theme: {
            background: '#1e1e1e',
            foreground: '#d4d4d4',
            cursor: '#aeafad',
            selectionBackground: '#264f78',
            black: '#000000',
            red: '#cd3131',
            green: '#0dbc79',
            yellow: '#e5e510',
            blue: '#2472c8',
            magenta: '#bc3fbc',
            cyan: '#11a8cd',
            white: '#e5e5e5',
            brightBlack: '#666666',
            brightRed: '#f14c4c',
            brightGreen: '#23d18b',
            brightYellow: '#f5f543',
            brightBlue: '#3b8eea',
            brightMagenta: '#d670d6',
            brightCyan: '#29b8db',
            brightWhite: '#e5e5e5',
          },
          cursorBlink: true,
          fontSize: 14,
          fontFamily: 'Consolas, "Courier New", monospace',
          lineHeight: 1.2,
          allowTransparency: true,
        });

        fitAddon = new FitAddon();
        term.loadAddon(fitAddon);
      } catch {
        if (term) {
          try {
            term.dispose();
          } catch {
            /* ignore */
          }
        }
        isInitializingRef.current = false;
        scheduleRetry();
        return;
      }

      const openTerminal = () => {
        if (!isActive || !terminalRef.current || !term || !fitAddon) {
          term?.dispose();
          isInitializingRef.current = false;
          scheduleRetry();
          return;
        }

        if (!hostReady()) {
          term.dispose();
          isInitializingRef.current = false;
          scheduleRetry();
          return;
        }

        // Ensure the host element has actual rendered dimensions
        const rect = host.getBoundingClientRect();
        if (rect.width < 100 || rect.height < 100) {
          term.dispose();
          isInitializingRef.current = false;
          scheduleRetry();
          return;
        }

        // Wrap in try-catch and add global error handler for async errors from xterm
        const originalOnError = window.onerror;
        let errorOccurred = false;

        window.onerror = (message, source, lineno, colno, error) => {
          if (source?.includes('xterm') && String(message).includes('dimensions')) {
            errorOccurred = true;
            return true; // Prevent error from propagating
          }
          return originalOnError?.call(window, message, source, lineno, colno, error) ?? false;
        };

        try {
          term.open(host);

          // Restore original error handler after a delay
          window.setTimeout(() => {
            window.onerror = originalOnError;
          }, 500);

          if (errorOccurred) {
            throw new Error('xterm dimensions error caught');
          }
        } catch (err) {
          window.onerror = originalOnError;
          console.warn('xterm open error, retrying:', err);
          term.dispose();
          isInitializingRef.current = false;
          scheduleRetry();
          return;
        }

        initAttemptsRef.current = 0;
        terminalInstanceRef.current = term;
        fitAddonRef.current = fitAddon;
        isInitializingRef.current = false;

        const detachResize = connectWebSocket(term, fitAddon);
        registerTerminalListeners(term);

        cleanupCallbacks.push(() => {
          detachResize?.();
        });
      };

      // Use setTimeout instead of requestAnimationFrame for more reliable timing
      window.setTimeout(() => {
        if (!isActive) return;
        openTerminal();
      }, 150);
    };

    const cleanupCallbacks: Array<() => void> = [];

    const scheduleRetry = () => {
      if (!isActive) return;
      initAttemptsRef.current += 1;

      if (initAttemptsRef.current > 10) {
        setError(t('pbxCli.terminal.messages.notConnected'));
        return;
      }

      retryTimeout = window.setTimeout(() => {
        if (!isActive) return;
        requestAnimationFrame(attemptInitialization);
      }, 200);
    };

    initTimeout = window.setTimeout(() => {
      requestAnimationFrame(attemptInitialization);
    }, 120);

    return () => {
      isActive = false;

      if (initTimeout) window.clearTimeout(initTimeout);
      if (retryTimeout) window.clearTimeout(retryTimeout);
      if (fitTimeout) window.clearTimeout(fitTimeout);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);

      isInitializingRef.current = false;

      cleanupCallbacks.forEach((cb) => cb());

      closeWebSocket();
      disposeTerminal();
    };
  }, [fsCliCommands, t]);

  const sendCommand = (command: string) => {
    const term = terminalInstanceRef.current;
    if (!term) return;

    const trimmed = command.trim();

    if (trimmed.toLowerCase() === 'clear') {
      term.clear();
      commandHistoryRef.current.push(trimmed);
      if (commandHistoryRef.current.length > 100) {
        commandHistoryRef.current.shift();
      }
      historyIndexRef.current = -1;
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      const message = t('pbxCli.terminal.messages.notConnected');
      setError(message);
      showError(message);
      return;
    }

    term.write(`${command}\r\n`);
    wsRef.current.send(`${command}\r`);
    commandHistoryRef.current.push(command);
    if (commandHistoryRef.current.length > 100) {
      commandHistoryRef.current.shift();
    }
    historyIndexRef.current = -1;
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        <div
          className="fixed top-[80px] z-40 rounded-lg border border-border/50 bg-card/75 p-3 shadow-lg backdrop-blur-md"
          style={{ left: `${effectiveSidebarWidth + 1}px`, right: '1px' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TerminalIcon className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-semibold text-foreground">{pageTitle}</h1>
            </div>
            <div className="flex items-center gap-2 text-sm">
              {isConnected ? (
                <span className="flex items-center gap-2 text-green-600">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-green-600" />
                  {t('pbxCli.connected')}
                </span>
              ) : (
                <span className="flex items-center gap-2 text-red-600">
                  <span className="h-2 w-2 rounded-full bg-red-600" />
                  {t('pbxCli.disconnected')}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="h-[10px]" />

        <div className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          )}

          <div className="rounded-lg bg-gray-900 p-4">
            <div ref={terminalRef} className="w-full" style={{ minHeight: '540px', height: '540px' }} />
          </div>

          <div className="rounded-lg border-t bg-card p-4">
            <div className="overflow-y-auto">
              <PbxCommandSlotGrid
                slots={buttonSlots}
                isConnected={isConnected}
                isLoading={loadingConfigs}
                onExecute={sendCommand}
                onEdit={setEditingSlot}
                t={t}
              />
            </div>
          </div>
        </div>
      </div>

      {editingSlot !== null && (
        <ButtonConfigModal
          isOpen={editingSlot !== null}
          onClose={() => setEditingSlot(null)}
          onSave={() => {
            loadButtonConfigs();
            setEditingSlot(null);
          }}
          slotIndex={editingSlot}
          existingConfig={buttonSlots[editingSlot]?.config || null}
        />
      )}
    </div>
  );
}

